// @ts-nocheck — expanded section prop surface vs inferred JSX props
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Link, useLocation, useNavigate, useParams } from "react-router-dom"
import { BoxLabels, BoxLabelsProgressModal } from "../box-labels/index.js"
import V2PageContainer from "../common/V2PageContainer.jsx"
import Toast from "../common/Toast.jsx"
import * as OrderForm from "./index.js"
import * as LocationUtils from "../utils/locations.js"
import { viewPrintOrder } from "../utils/viewPrintOrder.js"
import { fetchSignatureBlob, isUsableSignatureBlob } from "../utils/fetchSignatureBlob.js"
import { focusElement } from "../utils/scrollFocusedElement.js"
import { withFrozen } from "../utils/processes.js"
import { OrderFormApiProvider } from "./orderFormApiContext.jsx"
import { shippingFieldsFromShipTo } from "./shippingAddressUtils.js"
import { ContactService } from "../services/contactService.js"
import { CheckCircle2, Eye, Plus, Printer, WandSparkles } from "lucide-react"
import { stripCommasFromName } from "./nameCommaValidation.js"
import { snapshotAddressBaseline, snapshotContactBaseline } from "./customerFormBaseline.js"
import {
    isNewOrderFormDirty,
    isOrderFormDirtyVsBaseline,
    snapshotOrderFormDirtyState,
} from "./isNewOrderFormDirty.js"
import { useUnsavedChangesBlocker } from "../common/UnsavedChangesGuard.jsx"
import {
    ORDER_FORM_SECTION,
    errorsForSection,
    fieldErrorsForSection,
    firstOrderFormError,
    formatOrderFormSectionIssueSummary,
    isReviewAcknowledgementError,
    validateOrderForm,
    withoutReviewAcknowledgementErrors,
} from "./orderFormValidation.js"

const TEST_ORDERS_ENABLED = import.meta.env.VITE_TEST_ORDERS_ENABLED == "true"

const SECTION_COUNT = 6
const ALL_SECTIONS = Array.from({ length: SECTION_COUNT }, (_, i) => i)
const EXPAND_ALL_STORAGE_KEY = "stjeans_order_form_expand_all"

function readExpandAllPref() {
    try {
        return localStorage.getItem(EXPAND_ALL_STORAGE_KEY) === "true"
    } catch {
        return false
    }
}

function saveExpandAllPref(value) {
    try {
        localStorage.setItem(EXPAND_ALL_STORAGE_KEY, String(value))
    } catch {
        /* ignore */
    }
}

/**
 * @param {{
 *   app: Record<string, unknown>,
 *   api: Record<string, unknown>,
 *   enableDropOffLocationSelect?: boolean,
 *   enableMagicWandTool?: boolean,
 *   enablePrepaidAmount?: boolean,
 *   getOrderFromTemplateFunc?: ((orderId: number|string) => Promise<any>) | null,
 * }} props
 * When `enableDropOffLocationSelect` is false (e.g. lodge), drop-off location is fixed to `app.locationId`
 * using the full `app.locations` list and the location control is read-only.
 */
export default function NewOrderFormPage({
    app,
    api,
    enableDropOffLocationSelect = true,
    enableMagicWandTool = true,
    enablePrepaidAmount = true,
    getOrderFromTemplateFunc = null,
}) {
    const location = useLocation()
    const navigate = useNavigate()
    const params = useParams()
    const [expandAll, setExpandAll] = useState(() => readExpandAllPref())
    const [openSections, setOpenSections] = useState(() =>
        readExpandAllPref() ? ALL_SECTIONS : [0]
    )
    /**
     * Per accordion index: the section has been opened AND later closed at least once.
     * Used for the “✓ Complete” marker (we only show completion after the user has
     * meaningfully interacted with the section, not merely because it is open).
     */
    const [sectionsTouched, setSectionsTouched] = useState(/** @type {Record<number, boolean>} */({}))
    /** @type {Record<number | "dropoff", boolean>} */
    const [challenged, setChallenged] = useState(() => ({
        dropoff: false,
        0: false,
        1: false,
        2: false,
        3: false,
        4: false,
        5: false,
    }))
    /** After Review viewport or Submit, show the full Review error summary. */
    const [formWideChallenge, setFormWideChallenge] = useState(false)
    /** Terms / signature field chrome — only after Submit (not Review viewport). */
    const [reviewAckChallenged, setReviewAckChallenged] = useState(false)
    const [activationFocus, setActivationFocus] = useState(
        /** @type {{ index: number, mode: "first" | "nextRequired" } | null} */(null)
    )
    const [toastMessage, setToastMessage] = useState("")
    const [showToast, setShowToast] = useState(false)
    const [toastType, setToastType] = useState(/** @type {"info" | "warning" | "error" | "success"} */("warning"))
    const [toastTitle, setToastTitle] = useState("Not implemented")
    const [toastAutoDismissMs, setToastAutoDismissMs] = useState(/** @type {number | null} */(2600))
    const [toastVersion, setToastVersion] = useState(0)
    const [contact, setContact] = useState(() => OrderForm.defaultContact())
    const [delivery, setDelivery] = useState(() => OrderForm.defaultDelivery())
    const [shipping, setShipping] = useState(() => OrderForm.defaultShipping())
    const [billing, setBilling] = useState(() => OrderForm.defaultBilling())
    const [dropoff, setDropoff] = useState(() => OrderForm.defaultDropoff(getTodayLocalYyyyMmDd()))
    const [processing, setProcessing] = useState(() => OrderForm.defaultProcessing())
    const [signatureBlob, setSignatureBlob] = useState(/** @type {Blob | null} */(null))
    const [signatureClearedByUser, setSignatureClearedByUser] = useState(false)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [createdOrder, setCreatedOrder] = useState(null)
    const successPanelRef = useRef(/** @type {HTMLDivElement | null} */(null))
    const viewOrderButtonRef = useRef(/** @type {HTMLButtonElement | null} */(null))
    const [labelsToPrint, setLabelsToPrint] = useState(/** @type {any[]} */([]))
    const [renderHiddenPdf, setRenderHiddenPdf] = useState(false)
    const [showBoxLabelsModal, setShowBoxLabelsModal] = useState(false)
    const [boxLabelProgress, setBoxLabelProgress] = useState(0)
    const cancelBoxLabelsRef = useRef(false)

    const sectionRefs = useRef(/** @type {(HTMLDivElement | null)[]} */([]))
    const dropoffFieldsRef = useRef(/** @type {HTMLDivElement | null} */(null))
    const didPrefillCustomerRef = useRef(false)
    const didPrefillOrderRef = useRef(false)
    const prevOpenSectionsRef = useRef(/** @type {number[]} */([]))
    const [orderPrefill, setOrderPrefill] = useState(null)
    const [suppressContactNextFocus, setSuppressContactNextFocus] = useState(false)
    const lastLicensePrefillCustomerIdRef = useRef(/** @type {number | string | null} */(null))
    const prefillAssetsCancelRef = useRef(/** @type {(() => void) | null} */(null))
    const [editDirtyBaseline, setEditDirtyBaseline] = useState(
        /** @type {ReturnType<typeof snapshotOrderFormDirtyState> | null} */(null)
    )
    const [editBaselineVersion, setEditBaselineVersion] = useState(0)
    const formStateRef = useRef({
        contact,
        delivery,
        shipping,
        billing,
        dropoff,
        processing,
        signatureBlob,
    })
    formStateRef.current = {
        contact,
        delivery,
        shipping,
        billing,
        dropoff,
        processing,
        signatureBlob,
    }

    const [orderTemplates, setOrderTemplates] = useState(null) // null = loading, [] = loaded empty
    const [isAutofillMenuOpen, setIsAutofillMenuOpen] = useState(false)
    const [isAutofillingFromTemplate, setIsAutofillingFromTemplate] = useState(false)
    const autofillDropdownMobileRef = useRef(/** @type {HTMLDivElement | null} */(null))
    const autofillDropdownDesktopRef = useRef(/** @type {HTMLDivElement | null} */(null))

    const isEditingOrder = useMemo(() => /^\d+$/.test(String(params.orderId ?? "").trim()), [params.orderId])
    const orderHadServerSignature = useMemo(() => {
        const o = orderPrefill
        if (!o) return false
        if (String(o.signature_img ?? "").trim() !== "") return true
        const files = Array.isArray(o.files) ? o.files : []
        return files.some((f) => String(f?.type ?? "").trim().toLowerCase() === "signature")
    }, [orderPrefill])
    const requireSignature = !(isEditingOrder && orderHadServerSignature && !signatureClearedByUser)
    const formHasUnsavedData = useMemo(() => {
        if (createdOrder) {
            return false
        }
        if (isEditingOrder) {
            return isOrderFormDirtyVsBaseline(
                { contact, delivery, shipping, billing, dropoff, processing, signatureBlob },
                editDirtyBaseline
            )
        }
        return isNewOrderFormDirty({
            contact,
            delivery,
            shipping,
            billing,
            dropoff,
            processing,
            signatureBlob,
            todayYyyyMmDd: getTodayLocalYyyyMmDd(),
        })
    }, [
        isEditingOrder,
        createdOrder,
        editDirtyBaseline,
        contact,
        delivery,
        shipping,
        billing,
        dropoff,
        processing,
        signatureBlob,
    ])
    useUnsavedChangesBlocker(formHasUnsavedData)

    useEffect(() => {
        if (!isEditingOrder || editBaselineVersion === 0) {
            return
        }
        // Wait a tick so post-prefill section effects (address mirrors, etc.) can settle.
        const timer = window.setTimeout(() => {
            setEditDirtyBaseline(snapshotOrderFormDirtyState(formStateRef.current))
        }, 50)
        return () => window.clearTimeout(timer)
    }, [editBaselineVersion, isEditingOrder])
    const depots = useMemo(() => LocationUtils.getDepots(app.locations ?? []), [app.locations])
    const orderOriginLocationId = useMemo(() => {
        if (isEditingOrder && orderPrefill) {
            return LocationUtils.getOrderOriginLocationId(orderPrefill) ?? app.locationId
        }
        return app.locationId
    }, [isEditingOrder, orderPrefill, app.locationId])
    const pickupLocations = useMemo(
        () => LocationUtils.getPickupLocations(orderOriginLocationId, app.locations ?? []),
        [orderOriginLocationId, app.locations]
    )
    const dropoffLocationList = useMemo(
        () => (enableDropOffLocationSelect ? depots : (app.locations ?? [])),
        [enableDropOffLocationSelect, depots, app.locations]
    )
    const processesWithFrozen = useMemo(
        () => withFrozen(app.processes ?? [], app.weighInProcesses ?? []),
        [app.processes, app.weighInProcesses]
    )

    const allowLodgeDropoff = !enableDropOffLocationSelect

    const validationErrors = useMemo(
        () =>
            validateOrderForm({
                contact,
                delivery,
                shipping,
                billing,
                dropoff,
                processing,
                signatureBlob,
                termsAccepted: Boolean(contact?.termsAccepted),
                requireSignature,
                processes: processesWithFrozen,
                speciesOptions: app.species ?? [],
                optionsDataset: app.options ?? [],
                pickupLocations,
                dropoffLocations: dropoffLocationList,
                allowLodgeDropoff,
            }),
        [
            contact,
            delivery,
            shipping,
            billing,
            dropoff,
            processing,
            signatureBlob,
            requireSignature,
            processesWithFrozen,
            app.species,
            app.options,
            pickupLocations,
            dropoffLocationList,
            allowLodgeDropoff,
        ]
    )

    const showAutofillFromOrder =
        enableMagicWandTool &&
        Boolean(TEST_ORDERS_ENABLED) &&
        !isEditingOrder &&
        Boolean(app?.loaded)

    useEffect(() => {
        if (!createdOrder) {
            return
        }
        const el = successPanelRef.current
        if (!el) {
            return
        }
        document.activeElement?.blur?.()
        window.setTimeout(() => {
            window.requestAnimationFrame(() => {
                el.scrollIntoView({ block: "start", behavior: "smooth" })
            })
        }, 50)
    }, [createdOrder])

    useEffect(() => {
        if (!createdOrder) {
            return
        }
        // After the success panel renders, move focus to the primary CTA.
        window.setTimeout(() => {
            viewOrderButtonRef.current?.focus?.({ preventScroll: true })
        }, 0)
    }, [createdOrder])

    useEffect(() => {
        if (!showBoxLabelsModal) {
            setRenderHiddenPdf(false)
            setLabelsToPrint([])
            setBoxLabelProgress(0)
        }
    }, [showBoxLabelsModal])

    useEffect(() => {
        if (!showBoxLabelsModal) {
            return
        }
        if (boxLabelProgress !== 100) {
            return
        }
        const timeoutId = window.setTimeout(() => {
            setShowBoxLabelsModal(false)
        }, 500)
        return () => window.clearTimeout(timeoutId)
    }, [boxLabelProgress, showBoxLabelsModal])

    const processingDisabled = useMemo(
        () => delivery.method === "pickup" && Boolean(delivery.holdForPickup),
        [delivery.method, delivery.holdForPickup]
    )

    const clearActivationFocus = useCallback(() => setActivationFocus(null), [])

    const markAllSectionsTouched = useCallback(() => {
        setSectionsTouched(() => {
            const next = {}
            for (let i = 0; i < SECTION_COUNT; i += 1) {
                next[i] = true
            }
            return next
        })
    }, [])

    const prefillFromOrder = useCallback(
        async (o, { includeDropoffDate = true, captureEditBaseline = false } = {}) => {
            if (!o || !o.id) {
                return
            }

            prefillAssetsCancelRef.current?.()

            setSuppressContactNextFocus(true)
            setSignatureClearedByUser(false)
            markAllSectionsTouched()

            const orderDate = o.ordered_at?.split?.("T")?.[0] ?? ""
            const isPickup = Boolean(o.pick_up_location_id)
            const holdForPickup = Boolean(o.hold_for_pick_up ?? o.holdForPickup ?? false)

            const orderContactLike = {
                firstName: stripCommasFromName(
                    o.first_name ?? o.ship_to?.first_name ?? o.bill_to?.first_name ?? ""
                ),
                lastName: stripCommasFromName(
                    o.last_name ?? o.ship_to?.last_name ?? o.bill_to?.last_name ?? ""
                ),
                companyName: o.company ?? o.ship_to?.company ?? o.bill_to?.company ?? "",
                email: o.email ?? o.ship_to?.email ?? o.bill_to?.email ?? "",
                phone: o.phone ?? o.ship_to?.phone ?? o.bill_to?.phone ?? "",
            }

            setContact((prev) => ({
                ...prev,
                termsAccepted: true,
                customerId: o.customer_id ?? prev.customerId ?? null,
                firstName: stripCommasFromName(
                    o.first_name ?? o.ship_to?.first_name ?? o.bill_to?.first_name ?? prev.firstName
                ),
                lastName: stripCommasFromName(
                    o.last_name ?? o.ship_to?.last_name ?? o.bill_to?.last_name ?? prev.lastName
                ),
                email: o.email ?? o.ship_to?.email ?? o.bill_to?.email ?? prev.email,
                phone: o.phone ?? o.ship_to?.phone ?? o.bill_to?.phone ?? prev.phone,
                companyName: o.company ?? o.ship_to?.company ?? o.bill_to?.company ?? prev.companyName,
                contact_method: o.contact_method ?? prev.contact_method,
                is_staff: Boolean(o.is_staff ?? prev.is_staff),
                tripCode: o.trip_code ?? prev.tripCode,
                fishTag: o.fish_tag ?? prev.fishTag,
                licenseNumber: o.fishing_license ?? prev.licenseNumber ?? "",
                licenseFrontImg: o.license_front_img ?? prev.licenseFrontImg ?? null,
                licenseBackImg: o.license_back_img ?? prev.licenseBackImg ?? null,
                prepaidAmount: String(o.prepaid_amount ?? prev.prepaidAmount ?? "0"),
                subscribe_newsletter: Boolean(o.subscribe_newsletter ?? prev.subscribe_newsletter),
                ship_to: o.ship_to ?? prev.ship_to ?? null,
                default_ship_id: o.src_ship_id ?? prev.default_ship_id ?? null,
                bill_to: o.bill_to ?? prev.bill_to ?? null,
                default_bill_id: o.src_bill_id ?? prev.default_bill_id ?? null,
                bill_same_as_ship: Boolean(o.bill_same_as_ship ?? prev.bill_same_as_ship),
                updateCustomerFields: false,
                contactBaseline: null,
                selectedCustomerLabel: (() => {
                    const first = stripCommasFromName(
                        o.first_name ?? o.ship_to?.first_name ?? o.bill_to?.first_name ?? ""
                    )
                    const last = stripCommasFromName(
                        o.last_name ?? o.ship_to?.last_name ?? o.bill_to?.last_name ?? ""
                    )
                    return [first, last].filter(Boolean).join(" ").trim() || null
                })(),
            }))

            setDelivery((prev) => ({
                ...prev,
                method: isPickup ? "pickup" : "ship",
                pickupLocationId: isPickup ? (o.pick_up_location_id ?? null) : null,
                holdForPickup,
            }))

            setDropoff((prev) => ({
                ...prev,
                locationId: o.location_id ?? prev.locationId ?? null,
                date: includeDropoffDate ? (orderDate || prev.date) : prev.date,
            }))

            let cancelled = false
            prefillAssetsCancelRef.current = () => {
                cancelled = true
            }

            function normalizeFileType(raw) {
                return String(raw ?? "").trim().toLowerCase()
            }

            function getFileUrlByType(type) {
                const files = Array.isArray(o.files) ? o.files : []
                const match = files.find((f) => normalizeFileType(f?.type) === type)
                const url = match?.url ?? match?.URL ?? null
                return url ? String(url) : null
            }

            async function fetchStaticFile({ url, legacyPath, kind }) {
                const finalUrl =
                    (url != null && String(url).trim() !== "") ? String(url) :
                        legacyPath ? `/static/${legacyPath}` : null
                if (!finalUrl) return null

                if (kind === "signature") {
                    const blob = await fetchSignatureBlob(finalUrl, {
                        orderId: o?.id,
                        context: "NewOrderFormPage.prefillSignature",
                    })
                    return blob ? { blob, contentType: blob.type || "" } : null
                }

                try {
                    const res = await fetch(finalUrl)
                    if (!res.ok) {
                        console.log("Order form asset preload failed.", {
                            kind: kind ?? null,
                            url: finalUrl,
                            orderId: o?.id ?? null,
                            status: res.status,
                            statusText: res.statusText,
                        })
                        return null
                    }
                    const contentType = res.headers.get("Content-Type") ?? ""
                    const blob = await res.blob()
                    if (!(blob instanceof Blob) || blob.size <= 0) {
                        console.log("Order form asset preload returned empty file.", {
                            kind: kind ?? null,
                            url: finalUrl,
                            orderId: o?.id ?? null,
                            size: blob?.size ?? null,
                        })
                        return null
                    }
                    return { blob, contentType }
                } catch (error) {
                    console.log("Order form asset preload error.", {
                        kind: kind ?? null,
                        url: finalUrl,
                        orderId: o?.id ?? null,
                        error,
                    })
                    return null
                }
            }

            // When editing an order, populate address sections from the order payload itself.
            // Guest/legacy orders can have no `customer_id` (and thus no customer-driven autofill),
            // but still have full `ship_to` / `bill_to` details that should hydrate the UI.
            const addressesPromise = Promise.allSettled([
                shippingFieldsFromShipTo(o.ship_to ?? null, orderContactLike),
                shippingFieldsFromShipTo(o.bill_to ?? null, orderContactLike),
            ]).then((results) => {
                if (cancelled) return
                const [shipRes, billRes] = results
                const shipFields = shipRes.status === "fulfilled" ? shipRes.value : null
                const billFields = billRes.status === "fulfilled" ? billRes.value : null

                if (shipFields) {
                    setShipping((prev) => {
                        const next = {
                            ...prev,
                            ...shipFields,
                            shippingCustomerId: o.customer_id ?? prev.shippingCustomerId ?? null,
                            defaultShipAddressId: o.src_ship_id ?? prev.defaultShipAddressId ?? null,
                            addressDirty: false,
                            attachAddressToCustomer: false,
                        }
                        next.addressBaseline = snapshotAddressBaseline(next)
                        return next
                    })
                } else {
                    setShipping((prev) => ({ ...prev, addressDirty: false }))
                }

                const billSameAsShip = Boolean(o.bill_same_as_ship ?? false)
                const effectiveBillFields = billSameAsShip ? (shipFields ?? billFields) : billFields
                if (effectiveBillFields) {
                    setBilling((prev) => {
                        const next = {
                            ...prev,
                            ...effectiveBillFields,
                            useShippingAddress: billSameAsShip,
                            billingCustomerId: o.customer_id ?? prev.billingCustomerId ?? null,
                            defaultBillAddressId: o.src_bill_id ?? prev.defaultBillAddressId ?? null,
                            addressDirty: false,
                            attachAddressToCustomer: false,
                        }
                        next.addressBaseline = snapshotAddressBaseline(next)
                        return next
                    })
                } else {
                    setBilling((prev) => ({ ...prev, addressDirty: false }))
                }
            })

            // Preload existing signature + license images into the form as File/Blob objects.
            // This mirrors v1 behavior (edit form shows previously uploaded assets).
            const assetsPromise = Promise.allSettled([
                fetchStaticFile({
                    url: getFileUrlByType("signature"),
                    legacyPath: o.signature_img,
                    kind: "signature",
                }),
                fetchStaticFile({
                    url: getFileUrlByType("license front"),
                    legacyPath: o.license_front_img,
                    kind: "license front",
                }),
                fetchStaticFile({
                    url: getFileUrlByType("license back"),
                    legacyPath: o.license_back_img,
                    kind: "license back",
                }),
            ]).then((results) => {
                if (cancelled) return
                const [sig, front, back] = results.map((r) =>
                    r.status === "fulfilled" ? r.value : null
                )

                if (isUsableSignatureBlob(sig?.blob)) {
                    setSignatureBlob(sig.blob)
                    setSignatureClearedByUser(false)
                }

                if (front?.blob instanceof Blob) {
                    const fileName =
                        o.license_front_img ??
                        (Array.isArray(o.files) ? o.files.find((f) => normalizeFileType(f?.type) === "license front")?.filename : null) ??
                        "license-front"
                    const f = new File([front.blob], String(fileName), {
                        type:
                            front.contentType ||
                            front.blob.type ||
                            "application/octet-stream",
                    })
                    setContact((prev) => ({
                        ...prev,
                        licenseFrontImg: o.license_front_img ?? prev.licenseFrontImg ?? null,
                        licenseFileFront: f,
                        licenseFile: f,
                    }))
                }

                if (back?.blob instanceof Blob) {
                    const fileName =
                        o.license_back_img ??
                        (Array.isArray(o.files) ? o.files.find((f) => normalizeFileType(f?.type) === "license back")?.filename : null) ??
                        "license-back"
                    const f = new File([back.blob], String(fileName), {
                        type:
                            back.contentType ||
                            back.blob.type ||
                            "application/octet-stream",
                    })
                    setContact((prev) => ({
                        ...prev,
                        licenseBackImg: o.license_back_img ?? prev.licenseBackImg ?? null,
                        licenseFileBack: f,
                        licenseFile: prev.licenseFileFront ?? f,
                    }))
                }
            })

            setProcessing((prev) => {
                const details = Array.isArray(o.details) ? o.details : []
                const instructions =
                    details.length > 0
                        ? details.map((d) => ({
                            speciesId: d.species_id ?? null,
                            processId: d.process_id ?? null,
                            quantity: d.num_fish ?? 0,
                            unit:
                                d.unit_type === "pieces"
                                    ? "Individual Pieces"
                                    : "Whole Fish",
                            additionalNotes: d.fish_description ?? "",
                        }))
                        : prev.instructions

                const optionsArray = Array.isArray(o.options) ? o.options : []
                const nextOptions = { ...(prev.options ?? {}) }
                for (const opt of optionsArray) {
                    const id = opt?.option_id
                    if (id == null) continue
                    nextOptions[String(id)] = Boolean(opt?.value)
                }

                return {
                    ...prev,
                    totalWeight: String(o.total_weight_lb ?? prev.totalWeight ?? ""),
                    containers: String(o.total_boxes ?? prev.containers ?? ""),
                    fresh: false,
                    frozen: true,
                    instructions,
                    options: nextOptions,
                }
            })

            setActivationFocus(null)
            setOpenSections(expandAll ? ALL_SECTIONS : [0])

            await Promise.all([addressesPromise, assetsPromise])
            if (captureEditBaseline && !cancelled) {
                setEditBaselineVersion((v) => v + 1)
            }
        },
        [markAllSectionsTouched, expandAll]
    )

    useEffect(() => {
        if (enableDropOffLocationSelect) {
            if (!depots.length) {
                return
            }
            const currentLocation = depots.find((l) => String(l.id) === String(app.locationId))
            if (!currentLocation) {
                return
            }
            setDropoff((prev) => {
                if (prev.locationId != null) {
                    return prev
                }
                const id = currentLocation.id
                return { ...prev, locationId: id == null ? null : Number(id) }
            })
            return
        }

        const all = app.locations ?? []
        if (!all.length) {
            return
        }
        const currentLocation = all.find((l) => String(l.id) === String(app.locationId))
        if (!currentLocation) {
            return
        }
        setDropoff((prev) => {
            const id = currentLocation.id
            const numId = id == null ? null : Number(id)
            if (prev.locationId === numId) {
                return prev
            }
            return { ...prev, locationId: numId }
        })
    }, [enableDropOffLocationSelect, app.locationId, depots, app.locations])

    useEffect(() => {
        setDropoff((prev) => {
            if (String(prev.date ?? "").trim() !== "") {
                return prev
            }
            return { ...prev, date: getTodayLocalYyyyMmDd() }
        })
    }, [])

    useEffect(() => {
        if (didPrefillCustomerRef.current) {
            return
        }
        const c = location.state?.prefillCustomer
        if (!c || !c.id) {
            return
        }
        didPrefillCustomerRef.current = true
        setContact((prev) => ({ ...prev, ...contactStateFromApiCustomer(c) }))
    }, [location.state])

    useEffect(() => {
        let cancelled = false
        const raw = String(params.orderId ?? "").trim()
        if (!/^\d+$/.test(raw)) {
            return
        }
        const id = Number(raw)
            ; (async () => {
                try {
                    const o = await api.getOrder(id)
                    console.log("Edit order prefill:", o)
                    if (!cancelled) {
                        setOrderPrefill(o ?? null)
                    }
                } catch (e) {
                    console.error("Failed to load order for edit:", e)
                    if (!cancelled) {
                        setOrderPrefill(null)
                    }
                }
            })()
        return () => {
            cancelled = true
        }
    }, [params.orderId])

    useEffect(() => {
        if (didPrefillOrderRef.current) {
            return
        }
        const o = orderPrefill ?? location.state?.prefillOrder
        if (!o || !o.id) {
            return
        }
        didPrefillOrderRef.current = true
        prefillFromOrder(o, { captureEditBaseline: isEditingOrder })
    }, [location.state, orderPrefill, prefillFromOrder, isEditingOrder])

    useEffect(() => {
        if (!showAutofillFromOrder) {
            return
        }

        let cancelled = false
        setOrderTemplates(null)
        api.getOrderTemplates()
            .then((templates) => {
                if (cancelled) return
                setOrderTemplates(Array.isArray(templates) ? templates : [])
            })
            .catch(() => {
                if (cancelled) return
                setOrderTemplates([])
            })

        return () => {
            cancelled = true
        }
    }, [showAutofillFromOrder])

    useEffect(() => {
        const prev = prevOpenSectionsRef.current
        prevOpenSectionsRef.current = openSections

        // A section is "touched" only once it has been opened and then closed.
        // We mark touched when a previously-open index is no longer open.
        const closed = prev.filter((idx) => !openSections.includes(idx))
        if (closed.length === 0) {
            return
        }
        setSectionsTouched((p) => {
            const next = { ...p }
            for (const idx of closed) {
                next[idx] = true
            }
            return next
        })
    }, [openSections])

    useEffect(() => {
        if (!processingDisabled) {
            return
        }
        // If the user enables hold-for-pickup while Processing is open, close Processing
        // without collapsing other expand-all sections.
        if (!openSections.includes(4)) {
            return
        }
        setOpenSections((prev) => {
            const next = prev.filter((i) => i !== 4)
            return next.length > 0 ? next : [1]
        })
    }, [processingDisabled, openSections])

    useEffect(() => {
        // New orders only: when a customer is selected, prefill Contact license number
        // from the customer's saved fishing license (if present).
        if (isEditingOrder) {
            return
        }
        const customerId = contact?.customerId ?? null
        if (customerId == null) {
            lastLicensePrefillCustomerIdRef.current = null
            return
        }
        // Don't overwrite a value the user already entered.
        if (String(contact?.licenseNumber ?? "").trim() !== "") {
            lastLicensePrefillCustomerIdRef.current = customerId
            return
        }
        // Avoid refetching for the same customer id in this session.
        if (String(lastLicensePrefillCustomerIdRef.current ?? "") === String(customerId)) {
            return
        }

        let cancelled = false
            ; (async () => {
                try {
                    const c = await api.getCustomer(customerId)
                    if (cancelled) return
                    const license = String(c?.fishing_license ?? "").trim()
                    if (!license) {
                        lastLicensePrefillCustomerIdRef.current = customerId
                        return
                    }
                    setContact((prev) => {
                        if (String(prev?.licenseNumber ?? "").trim() !== "") {
                            return prev
                        }
                        return { ...prev, licenseNumber: license }
                    })
                    lastLicensePrefillCustomerIdRef.current = customerId
                } catch (e) {
                    // Silent: customer selection should not hard-fail the form.
                    lastLicensePrefillCustomerIdRef.current = customerId
                }
            })()

        return () => {
            cancelled = true
        }
    }, [contact?.customerId, contact?.licenseNumber, isEditingOrder])

    function getTodayLocalYyyyMmDd() {
        const now = new Date()
        const yyyy = now.getFullYear()
        const mm = String(now.getMonth() + 1).padStart(2, "0")
        const dd = String(now.getDate()).padStart(2, "0")
        return `${yyyy}-${mm}-${dd}`
    }

    function showNotImplementedToast() {
        setToastType("warning")
        setToastTitle("Not implemented")
        setToastAutoDismissMs(2600)
        setToastMessage("This is not implemented yet.")
        setToastVersion((x) => x + 1)
        setShowToast(true)
    }

    function showErrorToast(message, title = "Error") {
        setToastType("error")
        setToastTitle(title)
        setToastAutoDismissMs(null)
        setToastMessage(String(message ?? "Unknown error"))
        setToastVersion((x) => x + 1)
        setShowToast(true)
    }

    function showSuccessToast(message, title = "Success") {
        setToastType("success")
        setToastTitle(title)
        setToastAutoDismissMs(2600)
        setToastMessage(String(message ?? "Done"))
        setToastVersion((x) => x + 1)
        setShowToast(true)
    }

    async function autofillFromTemplate(template) {
        if (!template?.order_id) {
            return
        }
        setIsAutofillMenuOpen(false)
        setIsAutofillingFromTemplate(true)
        try {
            const fetchOrder = getOrderFromTemplateFunc ?? api.getOrder
            const o = await fetchOrder(template.order_id)
            prefillFromOrder(o, { includeDropoffDate: false })
            showSuccessToast(`Autofilled from "${template?.name ?? "template"}".`, "Autofilled")
        } catch (e) {
            showErrorToast(e?.message ?? "Failed to autofill from order.", "Autofill failed")
        } finally {
            setIsAutofillingFromTemplate(false)
        }
    }

    useEffect(() => {
        if (!isAutofillMenuOpen) {
            return
        }

        function isInsideAutofillDropdown(target) {
            if (!(target instanceof Node)) {
                return false
            }
            const nodes = [autofillDropdownMobileRef.current, autofillDropdownDesktopRef.current]
            return nodes.some((el) => el && el.contains(target))
        }

        function onPointerDown(e) {
            if (isInsideAutofillDropdown(e.target)) {
                return
            }
            setIsAutofillMenuOpen(false)
        }

        // Capture phase so we close even if other handlers stop propagation.
        document.addEventListener("mousedown", onPointerDown, true)
        document.addEventListener("touchstart", onPointerDown, true)
        return () => {
            document.removeEventListener("mousedown", onPointerDown, true)
            document.removeEventListener("touchstart", onPointerDown, true)
        }
    }, [isAutofillMenuOpen])

    const reviewSummaryErrors = useMemo(() => {
        if (!formWideChallenge) {
            return []
        }
        return reviewAckChallenged
            ? validationErrors
            : withoutReviewAcknowledgementErrors(validationErrors)
    }, [formWideChallenge, reviewAckChallenged, validationErrors])

    if (!app?.loaded) {
        return (
            <div className="min-vh-100 bg-light">
                <div
                    className="container"
                    style={{ maxWidth: "896px", paddingTop: "3rem", paddingBottom: "3rem" }}
                >
                    <div className="text-center mb-5">
                        <h1 className="fs-1 fw-bold text-dark mb-2">Custom Salmon Processing Order</h1>
                        <p className="text-secondary">Loading…</p>
                    </div>
                </div>
            </div>
        )
    }

    function autofillFromOrderButton() {
        if (!showAutofillFromOrder) {
            return null
        }

        const isLoadingTemplates = orderTemplates === null
        const isBusy = isLoadingTemplates || isAutofillingFromTemplate
        const sorted = Array.isArray(orderTemplates)
            ? [...orderTemplates].sort((a, b) => String(a?.name ?? "").localeCompare(String(b?.name ?? "")))
            : []

        const menuEl = isAutofillMenuOpen ? (
            <div
                className="dropdown-menu dropdown-menu-end show mt-2 small"
                data-bs-popper="static"
                style={{
                    right: 0,
                    left: "auto",
                    maxHeight: "calc(100vh - 96px)",
                    overflowY: "auto",
                    width: "min(18rem, calc(100vw - 2rem))",
                    maxWidth: "calc(100vw - 2rem)",
                    fontSize: "0.8125rem",
                }}
            >
                <div className="px-3 py-2 fw-semibold text-body bg-body-secondary border-bottom border-light-subtle">
                    Autofill form from template. Training Only.
                </div>
                {isLoadingTemplates ? (
                    <button
                        type="button"
                        className="dropdown-item d-flex align-items-center border-top border-light-subtle"
                        disabled
                    >
                        <span
                            className="spinner-border spinner-border-sm me-2"
                            role="status"
                            aria-hidden="true"
                        />
                        Loading templates…
                    </button>
                ) : sorted.length === 0 ? (
                    <button
                        type="button"
                        className="dropdown-item border-top border-light-subtle"
                        disabled
                    >
                        No templates found
                    </button>
                ) : (
                    sorted.map((t) => (
                        <button
                            key={t.id ?? `${t.name}-${t.order_id}`}
                            type="button"
                            className="dropdown-item py-2 border-top border-light-subtle"
                            title={t?.name ?? ""}
                            onClick={() => autofillFromTemplate(t)}
                        >
                            {t?.name ?? "Unnamed template"}
                        </button>
                    ))
                )}
            </div>
        ) : null

        const buttonEl = (
            <button
                type="button"
                className="btn btn-warning shadow border rounded-circle d-inline-flex align-items-center justify-content-center"
                style={{ width: "56px", height: "56px" }}
                title="Autofill from order"
                aria-label="Autofill from order"
                aria-expanded={isAutofillMenuOpen ? "true" : "false"}
                disabled={isBusy}
                onClick={() => setIsAutofillMenuOpen((v) => !v)}
            >
                {isBusy ? (
                    <span
                        className="spinner-border spinner-border-sm"
                        role="status"
                        aria-hidden="true"
                    />
                ) : (
                    <WandSparkles size={28} aria-hidden />
                )}
            </button>
        )

        // Mobile has a sticky top ribbon (64px) from `v2/components/Layout.jsx`.
        // Render a separate fixed container so the button never sits under it.
        return (
            <>
                <div
                    className="d-lg-none position-fixed end-0 p-3"
                    style={{ top: "64px", zIndex: 1031 }}
                >
                    <div ref={autofillDropdownMobileRef} className="dropdown">
                        {buttonEl}
                        {menuEl}
                    </div>
                </div>
                <div
                    className="d-none d-lg-block position-fixed top-0 end-0 p-3 p-md-4"
                    style={{ zIndex: 1031 }}
                >
                    <div ref={autofillDropdownDesktopRef} className="dropdown">
                        {buttonEl}
                        {menuEl}
                    </div>
                </div>
            </>
        )
    }

    function scrollToTopAfterOrderSubmit() {
        if (OrderForm.ORDER_FORM_PROGRAMMATIC_SCROLL_ENABLED) {
            window.scrollTo({ top: 0, left: 0, behavior: "instant" })
        }
    }

    async function fetchCreatedOrder(orderId) {
        try {
            const full = await api.getOrder(orderId)
            setCreatedOrder(full ?? { id: orderId })
        } catch (e) {
            console.error("Failed to fetch created order:", e)
            setCreatedOrder({ id: orderId })
        }
    }

    async function handlePrintReceipt() {
        if (!createdOrder || !app) return
        try {
            await viewPrintOrder({ order: createdOrder, app })
        } catch (e) {
            showErrorToast(e?.message ?? "Failed to print receipt.", "Print failed")
        }
    }

    function handlePrintBoxLabels() {
        if (!createdOrder) return
        setBoxLabelProgress(0)
        cancelBoxLabelsRef.current = false
        setShowBoxLabelsModal(true)
        setLabelsToPrint([createdOrder])
        window.setTimeout(() => setRenderHiddenPdf(true), 200)
    }

    function handleCancelBoxLabels() {
        cancelBoxLabelsRef.current = true
        setRenderHiddenPdf(false)
        setShowBoxLabelsModal(false)
    }

    async function uploadFishingLicenseIfProvided(orderId, offlineHandleOverride = null) {
        const front = contact?.licenseFileFront ?? null
        const back = contact?.licenseFileBack ?? null

        const tasks = /** @type {{ side: "front" | "back", file: File }[]} */([])
        if (front instanceof File) tasks.push({ side: "front", file: front })
        if (back instanceof File) tasks.push({ side: "back", file: back })

        if (tasks.length === 0) {
            return
        }

        const failures = []
        const offlineHandle =
            offlineHandleOverride ??
            (isEditingOrder && Boolean(orderPrefill?.created_offline) ? (orderPrefill?.id ?? null) : null)
        for (const t of tasks) {
            try {
                if (t.side === "front") {
                    await api.uploadOrderLicenseFront(orderId, t.file, offlineHandle ? { offlineHandle } : undefined)
                } else {
                    await api.uploadOrderLicenseBack(orderId, t.file, offlineHandle ? { offlineHandle } : undefined)
                }
            } catch (e) {
                failures.push({
                    side: t.side,
                    message: e?.message ?? String(e ?? "Unknown error"),
                })
            }
        }

        if (failures.length > 0) {
            const summary = failures
                .map((f) => `${f.side}: ${f.message}`)
                .join("\n")
            throw new Error(`Fishing license upload failed.\n${summary}`)
        }
    }

    async function uploadSignatureIfProvided(orderId, offlineHandleOverride = null) {
        if (!isUsableSignatureBlob(signatureBlob)) {
            return
        }
        const offlineHandle =
            offlineHandleOverride ??
            (isEditingOrder && Boolean(orderPrefill?.created_offline) ? (orderPrefill?.id ?? null) : null)
        await api.postSignature(orderId, signatureBlob, offlineHandle ? { offlineHandle } : undefined)
    }

    async function submitOrder() {
        if (validationErrors.length > 0) {
            challengeErrors(validationErrors, { formWide: true, reviewAck: true })
            focusOrderFormError(firstOrderFormError(validationErrors))
            return
        }

        if (isSubmitting) return
        setIsSubmitting(true)
        try {
            const payload = OrderForm.buildOrderPayload({
                app,
                contact,
                delivery,
                shipping,
                billing,
                dropoff,
                processing,
            })
            const editingIdRaw = String(params.orderId ?? "").trim()
            const editingId = /^\d+$/.test(editingIdRaw) ? Number(editingIdRaw) : null

            // OMS v1 update sends the full order object (incl. `master`). Mirror that for v2 edits.
            const response = await (async () => {
                if (isEditingOrder && editingId != null && typeof api.updateOrder === "function") {
                    const baseOrder =
                        orderPrefill ??
                        location.state?.prefillOrder ??
                        await api.getOrder(editingId)

                    const updateBody = {
                        ...(baseOrder ?? {}),
                        ...payload,
                        id: baseOrder?.id ?? editingId,
                        master: baseOrder?.master ?? null,
                    }

                    const offlineHandle = Boolean(baseOrder?.created_offline) ? (baseOrder?.id ?? null) : null
                    return await api.updateOrder(editingId, updateBody, offlineHandle ? { offlineHandle } : undefined)
                }
                return await api.postOrderV2(payload)
            })()

            const saved = response?.data ?? response
            const orderId =
                isEditingOrder && editingId != null
                    ? editingId
                    : saved?.id != null ? Number(saved.id)
                        : saved?.master != null ? Number(saved.master)
                            : null

            if (!orderId || !Number.isFinite(orderId)) {
                throw new Error(
                    isEditingOrder
                        ? "Order updated, but no order id was available."
                        : "Order created, but no order id was returned."
                )
            }

            // If the create was queued offline, uploads must be dependent on the create request's handle.
            // v1 does this by passing `offlineHandle`, which becomes `X-Upstream-Handle` and is stored as `handle` in `api_queue`.
            const offlineHandleForAssets = Boolean(saved?.created_offline) ? (saved?.id ?? null) : null

            await uploadFishingLicenseIfProvided(orderId, offlineHandleForAssets)
            await uploadSignatureIfProvided(orderId, offlineHandleForAssets)
            showSuccessToast(
                isEditingOrder
                    ? `Order updated${saved?.master ? ` (#${saved.master})` : ""}.`
                    : `Order created${saved?.master ? ` (#${saved.master})` : ""}.`,
                isEditingOrder ? "Order updated" : "Order created"
            )
            await fetchCreatedOrder(orderId)
            scrollToTopAfterOrderSubmit()
        } catch (e) {
            console.error(e)
            showErrorToast(e?.message ?? "Failed to submit order.", "Submit failed")
            scrollToTopAfterOrderSubmit()
        } finally {
            setIsSubmitting(false)
        }
    }

    function contactStateFromApiCustomer(c) {
        const firstName = stripCommasFromName(c.first_name ?? "")
        const lastName = stripCommasFromName(c.last_name ?? "")
        const next = {
            customerId: c.id ?? null,
            firstName,
            lastName,
            email: c.email ?? "",
            phone: ContactService.formatPhone(c.phone ?? ""),
            companyName: c.company ?? "",
            contact_method: OrderForm.mapApiContactMethod(c.contact_method),
            is_staff: Boolean(c.is_staff),
            tripCode: c.trip_code ?? "",
            fishTag: c.fish_tag ?? "",
            licenseNumber: c.fishing_license ?? "",
            licenseFrontImg: c.license_front_img ?? null,
            licenseBackImg: c.license_back_img ?? null,
            licenseFile: null,
            licenseFileFront: null,
            licenseFileBack: null,
            ship_to: c.ship_to ?? null,
            default_ship_id: c.default_ship_id ?? null,
            bill_to: c.bill_to ?? null,
            default_bill_id: c.default_bill_id ?? null,
            bill_same_as_ship: Boolean(c.bill_same_as_ship),
            updateCustomerFields: false,
            selectedCustomerLabel:
                [firstName, lastName].filter(Boolean).join(" ").trim() || null,
        }
        next.contactBaseline = snapshotContactBaseline(next)
        return next
    }

    /**
     * After a section opens, snap its wrapper so the header sits near the top
     * (uses `scroll-margin-top` on the section). Instant jump, not animated.
     * @param {number} index
     * @param {{ delayMs?: number, followFocusedControl?: boolean }} [opts]
     */
    function scrollToSection(index, opts = {}) {
        const delayMs = opts.delayMs ?? 100
        window.setTimeout(() => {
            const section = sectionRefs.current[index]
            if (!section) {
                return
            }
            window.requestAnimationFrame(() => {
                section.scrollIntoView({
                    block: "start",
                    behavior: "instant",
                })
            })
        }, delayMs)
    }

    /** Open a section by index, wait for accordion layout, then snap header to top. */
    /**
     * @param {number} index
     * @param {{ skipActivationFocus?: boolean }} [opts]
     */
    function jumpToAccordionSection(index, opts = {}) {
        if (processingDisabled && index === 4) {
            return
        }
        if (opts.skipActivationFocus) {
            // Error-link jumps focus a specific field; don't race with "focus first".
            setActivationFocus(null)
        } else {
            setActivationFocus({ index, mode: "first" })
        }
        if (expandAll) {
            setOpenSections(
                processingDisabled ? ALL_SECTIONS.filter((i) => i !== 4) : ALL_SECTIONS
            )
        } else {
            setOpenSections([index])
        }
        scrollToSection(index, { delayMs: OrderForm.ORDER_FORM_ACCORDION_SCROLL_DELAY_MS })
    }

    function scrollToDropoffFields() {
        const el = dropoffFieldsRef.current
        if (!el) {
            return
        }
        window.requestAnimationFrame(() => {
            el.scrollIntoView({
                block: "start",
                behavior: "smooth",
            })
        })
    }

    /**
     * @param {import("./orderFormValidation.js").OrderFormError[] | Array<{ section: number | "dropoff" }>} errors
     * @param {{ formWide?: boolean, reviewAck?: boolean }} [opts]
     */
    function challengeErrors(errors, opts = {}) {
        if (opts.formWide) {
            setFormWideChallenge(true)
        }
        if (opts.reviewAck) {
            setReviewAckChallenged(true)
        }
        setChallenged((prev) => {
            const next = { ...prev }
            for (const e of errors ?? []) {
                next[e.section] = true
            }
            return next
        })
    }

    /**
     * @param {{ section: number | "dropoff", domId: string } | null | undefined} error
     */
    function focusOrderFormError(error) {
        if (!error) return
        if (error.section === ORDER_FORM_SECTION.DROPOFF) {
            scrollToDropoffFields()
        } else if (typeof error.section === "number") {
            jumpToAccordionSection(error.section, { skipActivationFocus: true })
        }
        // Wait for accordion open + paint before focusing (collapsed panels steal/fail focus).
        const delayMs = Math.max(OrderForm.ORDER_FORM_ACCORDION_SCROLL_DELAY_MS, 50)
        window.setTimeout(() => {
            window.requestAnimationFrame(() => {
                focusElement(document.getElementById(error.domId), {
                    // Always apply .focus(); scroll follow respects the programmatic flag.
                    enabled: true,
                    scrollIntoView: OrderForm.ORDER_FORM_PROGRAMMATIC_SCROLL_ENABLED,
                    delay: true,
                })
            })
        }, delayMs)
    }

    function handleReviewVisible() {
        // Viewport: challenge prior sections / summary, but not Terms / Signature yet.
        setFormWideChallenge(true)
        const priorErrors = withoutReviewAcknowledgementErrors(validationErrors)
        if (priorErrors.length > 0) {
            challengeErrors(priorErrors)
        }
    }

    /**
     * @param {number | "dropoff"} section
     */
    function sectionFieldErrors(section) {
        if (!challenged[section]) return null
        return fieldErrorsForSection(validationErrors, section)
    }

    /**
     * @param {number | "dropoff"} section
     */
    function sectionHasErrors(section) {
        return Boolean(challenged[section]) && errorsForSection(validationErrors, section).length > 0
    }

    /**
     * @param {number | "dropoff"} section
     */
    function sectionIssueStatusLabel(section) {
        if (!sectionHasErrors(section)) return null
        return formatOrderFormSectionIssueSummary(errorsForSection(validationErrors, section))
    }

    function handleSectionComplete(sectionIndex) {
        if (!(processingDisabled && sectionIndex === 4)) {
            const sectionErrors = errorsForSection(validationErrors, sectionIndex)
            if (sectionErrors.length > 0) {
                challengeErrors(sectionErrors)
                focusOrderFormError(sectionErrors[0])
                return
            }
        }
        let next = sectionIndex + 1
        // Hold-for-pickup skips Processing (section 4) → Review (section 5).
        if (processingDisabled && next === 4) {
            next = 5
        }
        setActivationFocus({ index: next, mode: "nextRequired" })
        if (expandAll) {
            setOpenSections(
                processingDisabled ? ALL_SECTIONS.filter((i) => i !== 4) : ALL_SECTIONS
            )
        } else {
            setOpenSections([next])
        }
        scrollToSection(next, { delayMs: OrderForm.ORDER_FORM_ACCORDION_SCROLL_DELAY_MS })
    }

    function handleExpandAllToggle(enabled) {
        setExpandAll(enabled)
        saveExpandAllPref(enabled)
        if (enabled) {
            setOpenSections(
                processingDisabled ? ALL_SECTIONS.filter((i) => i !== 4) : ALL_SECTIONS
            )
        } else {
            setOpenSections([0])
        }
        setActivationFocus(null)
    }

    function toggleSection(index) {
        if (expandAll) {
            setOpenSections((prev) => {
                const isOpen = prev.includes(index)
                if (isOpen) {
                    setActivationFocus(null)
                    return prev.filter((i) => i !== index)
                }
                setActivationFocus({ index, mode: "first" })
                scrollToSection(index, { delayMs: OrderForm.ORDER_FORM_ACCORDION_SCROLL_DELAY_MS })
                return [...prev, index].sort((a, b) => a - b)
            })
            return
        }
        setOpenSections((prev) => {
            const isOpen = prev.includes(index)
            if (isOpen) {
                setActivationFocus(null)
                return []
            }
            setActivationFocus({ index, mode: "first" })
            scrollToSection(index, { delayMs: OrderForm.ORDER_FORM_ACCORDION_SCROLL_DELAY_MS })
            return [index]
        })
    }

    const sections = [
        {
            title: "1. Contact Information",
            content: (
                <OrderForm.ContactSection
                    title="1. Contact Information"
                    value={contact}
                    onChange={/** @type {any} */ (setContact)}
                    onNext={() => handleSectionComplete(0)}
                    isOpen={openSections.includes(0)}
                    touched={Boolean(sectionsTouched[0])}
                    onHeaderClick={() => toggleSection(0)}
                    isActive={openSections.includes(0)}
                    enablePrepaidAmount={enablePrepaidAmount}
                    suppressProgrammaticNextFocus={suppressContactNextFocus}
                    onSuppressProgrammaticNextFocusConsumed={() =>
                        setSuppressContactNextFocus(false)
                    }
                    activationFocusMode={
                        activationFocus?.index === 0 ? activationFocus.mode : null
                    }
                    onActivationFocusConsumed={clearActivationFocus}
                    activationFocusDelayMs={OrderForm.ORDER_FORM_ACCORDION_SCROLL_DELAY_MS}
                    statusLabel={sectionIssueStatusLabel(0)}
                    fieldErrors={sectionFieldErrors(0)}
                    showFieldErrors={Boolean(challenged[0])}
                    hasErrors={sectionHasErrors(0)}
                />
            ),
        },
        {
            title: "2. Delivery Method",
            content: (
                <OrderForm.DeliverySection
                    title="2. Delivery Method"
                    value={delivery}
                    locations={pickupLocations}
                    containers={processing.containers}
                    onContainersChange={(next) =>
                        setProcessing((prev) => ({ ...prev, containers: next }))
                    }
                    onChange={setDelivery}
                    onNext={() => handleSectionComplete(1)}
                    isOpen={openSections.includes(1)}
                    touched={Boolean(sectionsTouched[1])}
                    onHeaderClick={() => toggleSection(1)}
                    isActive={openSections.includes(1)}
                    activationFocusMode={
                        activationFocus?.index === 1 ? activationFocus.mode : null
                    }
                    onActivationFocusConsumed={clearActivationFocus}
                    activationFocusDelayMs={OrderForm.ORDER_FORM_ACCORDION_SCROLL_DELAY_MS}
                    statusLabel={sectionIssueStatusLabel(1)}
                    fieldErrors={sectionFieldErrors(1)}
                    showFieldErrors={Boolean(challenged[1])}
                    hasErrors={sectionHasErrors(1)}
                />
            ),
        },
        {
            title: "3. Shipping Information",
            content: (
                <OrderForm.ShippingSection
                    title="3. Shipping Information"
                    value={shipping}
                    delivery={delivery}
                    contact={contact}
                    onChange={/** @type {any} */ (setShipping)}
                    onContactChange={/** @type {any} */ (setContact)}
                    onNext={() => handleSectionComplete(2)}
                    isOpen={openSections.includes(2)}
                    touched={Boolean(sectionsTouched[2])}
                    onHeaderClick={() => toggleSection(2)}
                    isActive={openSections.includes(2)}
                    onJumpToSection={(index) => jumpToAccordionSection(index)}
                    activationFocusMode={
                        activationFocus?.index === 2 ? activationFocus.mode : null
                    }
                    onActivationFocusConsumed={clearActivationFocus}
                    activationFocusDelayMs={OrderForm.ORDER_FORM_ACCORDION_SCROLL_DELAY_MS}
                    statusLabel={sectionIssueStatusLabel(2)}
                    fieldErrors={sectionFieldErrors(2)}
                    showFieldErrors={Boolean(challenged[2])}
                    hasErrors={sectionHasErrors(2)}
                />
            ),
        },
        {
            title: "4. Billing Information",
            content: (
                <OrderForm.BillingSection
                    title="4. Billing Information"
                    value={billing}
                    shipping={shipping}
                    delivery={delivery}
                    contact={contact}
                    onChange={/** @type {any} */ (setBilling)}
                    onContactChange={/** @type {any} */ (setContact)}
                    onNext={() => handleSectionComplete(3)}
                    isOpen={openSections.includes(3)}
                    touched={Boolean(sectionsTouched[3])}
                    onHeaderClick={() => toggleSection(3)}
                    isActive={openSections.includes(3)}
                    onJumpToSection={(index) => jumpToAccordionSection(index)}
                    activationFocusMode={
                        activationFocus?.index === 3 ? activationFocus.mode : null
                    }
                    onActivationFocusConsumed={clearActivationFocus}
                    activationFocusDelayMs={OrderForm.ORDER_FORM_ACCORDION_SCROLL_DELAY_MS}
                    statusLabel={sectionIssueStatusLabel(3)}
                    fieldErrors={sectionFieldErrors(3)}
                    showFieldErrors={Boolean(challenged[3])}
                    hasErrors={sectionHasErrors(3)}
                />
            ),
        },
        {
            title: "5. Processing Details",
            content: (
                <OrderForm.ProcessingSection
                    title="5. Processing Details"
                    value={processing}
                    speciesOptions={app.species}
                    processes={processesWithFrozen}
                    optionsDataset={app.options ?? []}
                    shippingCountry={shipping?.country ?? ""}
                    onChange={setProcessing}
                    onNext={() => handleSectionComplete(4)}
                    isOpen={openSections.includes(4)}
                    touched={Boolean(sectionsTouched[4])}
                    statusLabel={sectionIssueStatusLabel(4) ?? (processingDisabled ? "Not applicable" : null)}
                    disabled={processingDisabled}
                    holdForPickup={Boolean(delivery?.holdForPickup)}
                    onHeaderClick={() => {
                        if (processingDisabled) return
                        toggleSection(4)
                    }}
                    isActive={openSections.includes(4)}
                    activationFocusMode={
                        activationFocus?.index === 4 ? activationFocus.mode : null
                    }
                    onActivationFocusConsumed={clearActivationFocus}
                    activationFocusDelayMs={OrderForm.ORDER_FORM_ACCORDION_SCROLL_DELAY_MS}
                    fieldErrors={sectionFieldErrors(4)}
                    showFieldErrors={Boolean(challenged[4])}
                    hasErrors={sectionHasErrors(4)}
                />
            ),
        },
        {
            title: "6. Review & Submit",
            content: (
                <OrderForm.ReviewSection
                    title="6. Review & Submit"
                    model={{ contact, delivery, shipping, billing, dropoff, processing }}
                    speciesOptions={app.species}
                    processOptions={processesWithFrozen}
                    optionsDataset={app.options ?? []}
                    locations={dropoffLocationList}
                    onJumpTo={(index) => jumpToAccordionSection(index)}
                    onJumpToDropoff={scrollToDropoffFields}
                    onSubmit={submitOrder}
                    isSubmitting={isSubmitting}
                    submitLabel={isEditingOrder ? "Update Order" : "Submit Order"}
                    isOpen={openSections.includes(5)}
                    touched={Boolean(sectionsTouched[5])}
                    onHeaderClick={() => toggleSection(5)}
                    // Terms/signature only challenge after Submit (not on Review-in-view) — same gate as hasErrors.
                    statusLabel={
                        reviewAckChallenged &&
                        errorsForSection(validationErrors, ORDER_FORM_SECTION.REVIEW).length > 0
                            ? formatOrderFormSectionIssueSummary(
                                errorsForSection(validationErrors, ORDER_FORM_SECTION.REVIEW)
                            )
                            : null
                    }
                    enablePrepaidAmount={enablePrepaidAmount}
                    termsAccepted={Boolean(contact.termsAccepted)}
                    onTermsAcceptedChange={(v) =>
                        setContact((p) => ({ ...p, termsAccepted: Boolean(v) }))
                    }
                    signatureBlob={signatureBlob}
                    onSignatureBlobChange={(b) => {
                        if (isUsableSignatureBlob(b)) {
                            setSignatureBlob(b)
                            setSignatureClearedByUser(false)
                            return
                        }
                        setSignatureBlob(null)
                    }}
                    onSignatureClearedByUser={() => setSignatureClearedByUser(true)}
                    newsletterOptIn={Boolean(contact.subscribe_newsletter)}
                    onNewsletterOptInChange={(v) =>
                        setContact((p) => ({ ...p, subscribe_newsletter: Boolean(v) }))
                    }
                    validationErrors={reviewSummaryErrors}
                    hasErrors={
                        reviewAckChallenged &&
                        errorsForSection(validationErrors, ORDER_FORM_SECTION.REVIEW).length > 0
                    }
                    fieldErrors={
                        reviewAckChallenged
                            ? fieldErrorsForSection(validationErrors, ORDER_FORM_SECTION.REVIEW)
                            : null
                    }
                    showFieldErrors={reviewAckChallenged}
                    onReviewVisible={handleReviewVisible}
                    onJumpToError={(err) => {
                        challengeErrors([err], {
                            reviewAck: isReviewAcknowledgementError(err),
                        })
                        focusOrderFormError(err)
                    }}
                />
            ),
        },
    ]

    return (
        <OrderFormApiProvider api={api}>
            <V2PageContainer className="bg-light">
                {autofillFromOrderButton()}
                <Toast
                    key={toastVersion}
                    show={showToast}
                    type={toastType}
                    title={toastTitle}
                    message={toastMessage}
                    autoDismissMs={toastAutoDismissMs}
                    onClose={() => {
                        setShowToast(false)
                        setToastMessage("")
                    }}
                />
                <BoxLabels
                    orders={labelsToPrint}
                    locations={/** @type {any[]} */ (app.locations ?? [])}
                    openPdf={renderHiddenPdf}
                    setOpenPdf={setRenderHiddenPdf}
                    progress={boxLabelProgress}
                    setProgress={setBoxLabelProgress}
                    cancelRef={cancelBoxLabelsRef}
                    app={app}
                />
                <BoxLabelsProgressModal
                    show={showBoxLabelsModal}
                    progress={boxLabelProgress}
                    onCancel={handleCancelBoxLabels}
                />
                <div
                    className="container mx-auto w-100"
                    style={{ maxWidth: "896px", paddingBottom: "3rem" }}
                >
                    <div className="text-center mb-4">
                        <h1 className="fs-1 fw-bold text-dark mb-2">
                            Custom Salmon Processing Order
                        </h1>
                        <p className="text-secondary mb-0">
                            Please fill out the form below to submit your processing order
                        </p>
                    </div>

                    <div className="mb-3">
                        <OrderForm.DropoffSection
                            value={dropoff}
                            locations={dropoffLocationList}
                            locationSelectDisabled={!enableDropOffLocationSelect}
                            onChange={setDropoff}
                            cardRef={dropoffFieldsRef}
                            fieldErrors={sectionFieldErrors("dropoff")}
                        />
                    </div>

                    <div className="d-flex justify-content-end align-items-center gap-2 mb-3">
                        <label
                            htmlFor="order-form-expand-all"
                            className="form-check-label small text-secondary mb-0"
                        >
                            Expand all sections
                        </label>
                        <div className="form-check form-switch mb-0">
                            <input
                                id="order-form-expand-all"
                                type="checkbox"
                                className="form-check-input"
                                role="switch"
                                checked={expandAll}
                                onChange={(e) => handleExpandAllToggle(e.target.checked)}
                            />
                        </div>
                    </div>

                    <div className="d-flex flex-column gap-3">
                        {sections.map((section, index) => (
                            <div
                                key={section.title}
                                ref={(el) => {
                                    sectionRefs.current[index] = el
                                }}
                                style={{ scrollMarginTop: "6rem" }}
                            >
                                {section.content}
                            </div>
                        ))}
                    </div>

                    {createdOrder ? (
                        <div
                            ref={successPanelRef}
                            className="bg-secondary bg-opacity-10 rounded mt-4 mt-md-5 mb-5 mb-md-6 p-4 p-md-5"
                            style={{ scrollMarginTop: "6rem" }}
                        >
                            <div className="text-center">
                                <div className="mb-3 d-flex justify-content-center">
                                    <CheckCircle2
                                        className="text-success"
                                        size={80}
                                        strokeWidth={1.75}
                                        aria-hidden
                                    />
                                </div>
                                <div className="fs-5">
                                    <p className="mb-3">
                                        Success! Your order has been {isEditingOrder ? "updated" : "created"}.
                                    </p>
                                    <div className="text-secondary">Your order number is:</div>
                                    <div className="fw-bold display-5">
                                        #{createdOrder?.master ?? createdOrder?.id ?? "—"}
                                    </div>
                                </div>
                            </div>

                            <div className="mt-4 mt-md-5 d-flex justify-content-center">
                                <div className="w-100" style={{ maxWidth: "560px" }}>
                                    <div className="row g-3">
                                        <div className="col-12 col-md-6">
                                            <button
                                                type="button"
                                                className="btn btn-light fs-5 py-2 px-3 border w-100 d-inline-flex align-items-center justify-content-center"
                                                onClick={() => handlePrintReceipt()}
                                            >
                                                <Printer className="me-2 flex-shrink-0" size={22} aria-hidden />
                                                Print Receipt
                                            </button>
                                        </div>
                                        <div className="col-12 col-md-6">
                                            <button
                                                type="button"
                                                className="btn btn-light fs-5 py-2 px-3 border w-100 d-inline-flex align-items-center justify-content-center"
                                                onClick={() => handlePrintBoxLabels()}
                                                disabled={showBoxLabelsModal}
                                            >
                                                <Printer className="me-2 flex-shrink-0" size={22} aria-hidden />
                                                Print Box Labels
                                            </button>
                                        </div>
                                        <div className="col-12 col-md-6">
                                            <a
                                                href="/v2/order-form"
                                                className="btn btn-light fs-5 py-2 px-3 border w-100 d-inline-flex align-items-center justify-content-center"
                                                onClick={() => document.activeElement?.blur?.()}
                                            >
                                                <Plus className="me-2 flex-shrink-0" size={22} aria-hidden />
                                                Create Another
                                            </a>
                                        </div>
                                        <div className="col-12 col-md-6">
                                            <Link
                                                ref={viewOrderButtonRef}
                                                className="btn btn-primary fs-5 py-2 px-4 border w-100 d-inline-flex align-items-center justify-content-center"
                                                to={createdOrder?.id ? `/v2/orders/${createdOrder.id}/order` : "#"}
                                                state={{ preventScrollReset: false }}
                                                aria-disabled={createdOrder?.id ? undefined : "true"}
                                                tabIndex={createdOrder?.id ? undefined : -1}
                                                onClick={(e) => {
                                                    if (!createdOrder?.id) {
                                                        e.preventDefault()
                                                        return
                                                    }
                                                    document.activeElement?.blur?.()
                                                }}
                                            >
                                                <Eye className="me-2 flex-shrink-0" size={22} aria-hidden />
                                                View Order
                                            </Link>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : null}
                </div>
            </V2PageContainer>
        </OrderFormApiProvider>
    )
}
